import { useEffect, useRef, useState } from 'react'
import { useGeneralAgentChatStore } from '../../hooks/generalAgentChatStore'
import { revisionResultText, type OpenedRevision, type RevisionChoice } from '../uploads/uploadFlow'

/**
 * A rate con uploaded from a load's item that the board filed as a revision: the item shows it until the user picks, and the
 * chat is told once what came of it — the choice, or, when the item goes away first, that it still waits on Ongoing Loads.
 * Only the conversation it was uploaded in is told: a results item unmounts on every switch of conversation and the store
 * sends to the one open now, so a line due after a switch is dropped (the revision stays pending on the board).
 */
export function useRowRevision() {
    const notify = useGeneralAgentChatStore((s) => s.notify)
    const latestNotify = useRef(notify)
    const [revision, setRevision] = useState<OpenedRevision | null>(null)
    // The revision on show and whether its line was said: refs, so the unmount below reads them as they are.
    const shown = useRef<OpenedRevision | null>(null)
    const said = useRef(false)
    const openedOn = useRef<string | null>(null)
    const choosing = useRef(false)
    const mounted = useRef(true)

    useEffect(() => {
        latestNotify.current = notify
    }, [notify])

    const say = (choice: RevisionChoice) => {
        if (!shown.current || said.current) return
        said.current = true
        if (useGeneralAgentChatStore.getState().tabId !== openedOn.current) return
        latestNotify.current(revisionResultText(shown.current, choice))
    }

    // Gone without a choice: the revision stays pending on the board. A choice still running says its own line when it ends.
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
            if (!choosing.current) say({ kind: 'left' })
        }
    }, [])

    const open = (opened: OpenedRevision) => {
        shown.current = opened
        openedOn.current = useGeneralAgentChatStore.getState().tabId
        said.current = false
        setRevision(opened)
    }

    const resolve = (choice: RevisionChoice) => {
        say(choice)
        shown.current = null
        if (mounted.current) setRevision(null)
    }

    const onSubmittingChange = (running: boolean) => {
        choosing.current = running
        // Unmounted while a choice ran and it failed: nothing was decided, so the revision still waits on the board.
        if (!running && !mounted.current) say({ kind: 'left' })
    }

    return { revision, open, resolve, onSubmittingChange }
}
